import { useEffect, useState } from 'react';
import { EngineHttpError, engineFetchJson } from '@/lib/engineHttp';
import type { WidgetDefinition, WidgetProps } from '@/types/widget';

interface ServiceStatus {
  name: string;
  url: string;
  status: 'ok' | 'error' | 'checking';
  latencyMs: number | null;
  detail?: string;
}

interface EndpointTarget {
  name: string;
  target: string;
  kind: 'engine' | 'direct';
}

function statusColor(status: string): string {
  switch (status) {
    case 'ok':
      return 'var(--sp-ok)';
    case 'error':
      return 'var(--sp-error)';
    case 'checking':
      return 'var(--sp-warn)';
    default:
      return 'var(--sp-text-dim)';
  }
}

async function checkEndpoint(target: EndpointTarget): Promise<ServiceStatus> {
  const start = performance.now();
  try {
    if (target.kind === 'engine') {
      await engineFetchJson(target.target, {
        signal: AbortSignal.timeout(5000),
      });
      const latencyMs = Math.round(performance.now() - start);
      return { name: target.name, url: target.target, status: 'ok', latencyMs };
    }
    const response = await fetch(target.target, {
      method: 'GET',
      signal: AbortSignal.timeout(5000),
    });
    const latencyMs = Math.round(performance.now() - start);
    if (response.ok) {
      return { name: target.name, url: target.target, status: 'ok', latencyMs };
    }
    return { name: target.name, url: target.target, status: 'error', latencyMs, detail: `HTTP ${response.status}` };
  } catch (e) {
    const latencyMs = Math.round(performance.now() - start);
    return {
      name: target.name,
      url: target.target,
      status: 'error',
      latencyMs: latencyMs > 4900 ? null : latencyMs,
      detail:
        e instanceof EngineHttpError
          ? `HTTP ${e.status}`
          : e instanceof Error
            ? e.message
            : 'Unknown error',
    };
  }
}

function SystemHealthWidget({ context }: WidgetProps) {
  const [services, setServices] = useState<ServiceStatus[] | null>(null);
  const [lastCheck, setLastCheck] = useState<Date | null>(null);

  useEffect(() => {
    const endpoints: EndpointTarget[] = [
      { name: 'Engine API', target: '/health', kind: 'engine' },
      { name: 'Health Summary', target: '/health/summary', kind: 'engine' },
      { name: 'Seitiate+ Backend', target: `${context.plusApiUrl}/health`, kind: 'direct' },
    ];
    if (context.isDirector) {
      endpoints.splice(2, 0, {
        name: 'Director Health',
        target: '/health/detailed',
        kind: 'engine',
      });
    }

    // Set initial "checking" state
    setServices(endpoints.map((e) => ({ name: e.name, url: e.target, status: 'checking' as const, latencyMs: null })));

    Promise.all(endpoints.map((e) => checkEndpoint(e))).then((results) => {
      setServices(results);
      setLastCheck(new Date());
    });

    // Re-check every 60 seconds
    const interval = setInterval(() => {
      Promise.all(endpoints.map((e) => checkEndpoint(e))).then((results) => {
        setServices(results);
        setLastCheck(new Date());
      });
    }, 60000);

    return () => clearInterval(interval);
  }, [context.authToken, context.isDirector, context.plusApiUrl]);

  const allOk = services?.every((s) => s.status === 'ok') ?? false;
  const anyError = services?.some((s) => s.status === 'error') ?? false;

  return (
    <div className="sp-widget">
      <div className="sp-widget-header">
        <span>System Health</span>
        {services && (
          <span style={{
            fontSize: 10,
            fontWeight: 400,
            textTransform: 'none',
            letterSpacing: 0,
            color: allOk ? 'var(--sp-ok)' : anyError ? 'var(--sp-error)' : 'var(--sp-warn)',
          }}>
            {allOk ? 'All systems operational' : anyError ? 'Degraded' : 'Checking...'}
          </span>
        )}
      </div>
      <div className="sp-widget-body" style={{ padding: '4px 12px' }}>
        {services ? (
          <div>
            {services.map((s) => (
              <div
                key={s.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 0',
                  fontSize: 12,
                  borderBottom: '1px solid var(--sp-border)',
                }}
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: statusColor(s.status),
                    flexShrink: 0,
                    animation: s.status === 'checking' ? 'pulse 1s ease-in-out infinite' : 'none',
                  }}
                />
                <span style={{ flex: 1 }}>{s.name}</span>
                {s.latencyMs !== null && (
                  <span className="mono" style={{ fontSize: 10, color: s.latencyMs > 1000 ? 'var(--sp-warn)' : 'var(--sp-text-dim)' }}>
                    {s.latencyMs}ms
                  </span>
                )}
                {s.status === 'error' && s.detail && (
                  <span className="mono" style={{ fontSize: 10, color: 'var(--sp-error)' }}>
                    {s.detail}
                  </span>
                )}
              </div>
            ))}

            {/* Infrastructure checks that would need backend support */}
            <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px solid var(--sp-border)' }}>
              <div style={{ fontSize: 10, color: 'var(--sp-text-dim)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Infrastructure (awaiting endpoints)
              </div>
              {['Cloud SQL', 'Cloud Storage', 'Redis Cache'].map((name) => (
                <div
                  key={name}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '4px 0',
                    fontSize: 12,
                  }}
                >
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--sp-border)', flexShrink: 0 }} />
                  <span style={{ color: 'var(--sp-text-dim)' }}>{name}</span>
                  <span className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', marginLeft: 'auto' }}>--</span>
                </div>
              ))}
            </div>

            {lastCheck && (
              <div style={{ fontSize: 10, color: 'var(--sp-text-dim)', marginTop: 6 }}>
                Last check: {lastCheck.toLocaleTimeString()} (auto-refresh 60s)
              </div>
            )}
          </div>
        ) : (
          <div style={{ color: 'var(--sp-text-muted)', fontSize: 12 }}>Checking services...</div>
        )}
      </div>
    </div>
  );
}

export const systemHealthWidget: WidgetDefinition = {
  id: 'system-health',
  name: 'System Health',
  description: 'Live health checks for all backend services.',
  icon: 'activity',
  category: 'admin',
  scope: 'director',
  defaultSize: { w: 6, h: 5 },
  minSize: { w: 4, h: 4 },
  component: SystemHealthWidget,
};
